import { NativeVideoClient } from './NativeVideoClient';
import { NATIVE_VIDEO_CHANNEL, parseNativeVideoMessage, type NativeVideoCodec, type NativeVideoMessage } from './NativeVideoProtocol';
import type { VideoPresentationTarget } from './VideoPresentationTypes';
import { WebCodecsClient } from '../webcodecs/client/WebCodecsClient';
import { WebCodecsCapabilities } from '../webcodecs/client/WebCodecsCapabilities';
import type { WcClientEvent, WcClientStats, WcWorkerPort } from '../webcodecs/client/WebCodecsClientTypes';
import type { WcConfig } from '../webcodecs/protocol/WebCodecsTypes';
import type { VideoStreamingStatus } from './NativeVideoHost';
import { webCodecsFallbackReason } from './NativeVideoRecoveryLadder';
import { paintSampleFrom, type WebCodecsPaintSample } from './WebCodecsPaintSample';

/** Guest-side path overlay uses a fixed viewer id; the host keys by peer id. */
export const GUEST_VIDEO_PATH_VIEWER_ID = 'guest';

interface PlayerPeer {
  get(): RTCPeerConnection;
  addDataChannel(label: string, options: RTCDataChannelInit): unknown;
  send(data: string, label: string): boolean;
  on(event: string, listener: (event: any) => void): unknown;
}

/** Native video owns a separate peer; primary audio and input survive every presentation change. */
export type PlayerVideoBridgeOptions = {
  createWebCodecsWorker(): WcWorkerPort;
  /** Every WebCodecs frame drawn on the canvas, for the calibrated latency probe. */
  onPresented?: (sample: WebCodecsPaintSample) => void;
};

export class PlayerVideoBridge {
  private nativeTrack: MediaStreamTrack | null = null;
  private primary: MediaStream | null = null;
  private closed = false;
  private client: NativeVideoClient;
  private target: VideoPresentationTarget | null = null;
  private webCodecs: WebCodecsClient | null = null;
  private config: WcConfig | null = null;
  private session = '';
  private generation = 0;
  private helloGeneration = 0;
  private channelOpen = false;
  private nativeOpen = false;
  private dataOpen = false;
  /** Locked to the channel that carried the first hello. Safari often never opens the second channel. */
  private signalingLabel: string | null = null;
  private dataHello: ReturnType<typeof setTimeout> | undefined;
  private helloDelay: ReturnType<typeof setTimeout> | undefined;
  private chain = Promise.resolve();
  private selectedCanvas = false;
  /** The host configured WebCodecs; it never sends conventional video in that mode, so none is presented. */
  private webCodecsSelected = false;
  private pathStatus: VideoStreamingStatus = {
    viewerId: GUEST_VIDEO_PATH_VIEWER_ID,
    active: 'electron',
    pending: false,
    sessionRestarts: 0,
    recoveryState: 'idle',
  };
  private readonly visibility = () => this.webCodecs?.setVisible(document.visibilityState !== 'hidden');

  constructor(private peer: PlayerPeer, private onStream: (stream: MediaStream) => void,
    private options?: PlayerVideoBridgeOptions) {
    this.client = new NativeVideoClient({
      send: message => this.send(message),
      createPeer: () => new RTCPeerConnection(peer.get().getConfiguration()),
      capabilities: () => RTCRtpReceiver.getCapabilities('video'),
      onVideo: track => {
        this.nativeTrack = track;
        if (track && !this.selectedCanvas) {
          this.patchPath({
            active: 'native',
            pending: false,
            playback: 'rtp',
            codec: this.pathStatus.requestedCodec,
            recoveryState: 'idle',
          });
        }
        if (!this.closed) this.publish();
      },
    });
    peer.on('channelOpen', event => {
      if (this.closed) return;
      const label = event.channel?.label;
      if (label === NATIVE_VIDEO_CHANNEL) {
        this.nativeOpen = true;
        this.channelOpen = true;
        this.clearDataHello();
        // A hello already sent on `data` stays there; the host is answering on that channel.
        if (this.signalingLabel !== 'data') void this.hello();
      } else if (label === 'data') {
        this.dataOpen = true;
        this.armDataHello();
      }
    });
    peer.on('channelData', event => {
      const label = event.channel?.label;
      if (this.closed || event.source === 'outgoing') return;
      if (label !== NATIVE_VIDEO_CHANNEL && !(label === 'data' && this.signalingLabel === 'data')) return;
      const message = parseNativeVideoMessage(event.data);
      if (!message) return;
      // Cancel asynchronous startup immediately, before queued SDP continuations run.
      if (message.type === 'configure') this.retire();
      else if (message.session === this.session && ['fallback', 'close'].includes(message.type)) this.retire();
      const generation = this.generation;
      this.chain = this.chain.then(async () => {
        if (!this.closed && generation === this.generation) await this.receive(message, event.data);
      }).catch(() => { if (!this.closed && generation === this.generation) this.fail('Native video negotiation failed'); });
    });
    peer.on('streamRemote', stream => { if (!this.closed) { this.primary = stream; this.publish(); } });
  }

  start(): void { this.peer.addDataChannel(NATIVE_VIDEO_CHANNEL, { ordered: true }); }

  /** Rebinding retires transferred canvas ownership and invalidates pending capability probes. */
  setPresentationTarget(target: VideoPresentationTarget | null): void {
    if (this.closed || this.target === target) return;
    if (this.session) this.send({ nativeVideo: 1, type: 'fallback', session: this.session, reason: 'Video surface detached' });
    this.retire();
    this.target?.releaseCanvas();
    this.target = target;
    this.publish();
    if (this.channelOpen || this.dataOpen) void this.hello();
  }

  /** Safari drops a second data channel. Greet on the gameplay channel if the dedicated one never opens. */
  private armDataHello(): void {
    if (this.nativeOpen || this.dataHello || this.signalingLabel) return;
    this.dataHello = setTimeout(() => {
      this.dataHello = undefined;
      if (this.closed || this.nativeOpen || !this.dataOpen) return;
      this.channelOpen = true;
      void this.hello();
    }, 500);
  }

  private clearDataHello(): void {
    if (this.dataHello !== undefined) clearTimeout(this.dataHello);
    this.dataHello = undefined;
  }

  private async hello(): Promise<void> {
    const generation = ++this.helloGeneration, target = this.target;
    const ready = this.hasWebCodecsSurface();
    const capabilities = new WebCodecsCapabilities({ isConfigSupported: config => VideoDecoder.isConfigSupported(config) });
    const codecs = await capabilities.families(ready);
    // Yield past the channel's open callback. Safari drops a message sent in that turn.
    await new Promise<void>(resolve => { this.helloDelay = setTimeout(resolve, 0); });
    if (this.closed || generation !== this.helloGeneration || target !== this.target) return;
    if (!this.signalingLabel) this.signalingLabel = this.nativeOpen ? NATIVE_VIDEO_CHANNEL : 'data';
    const message: NativeVideoMessage = { nativeVideo: 1, type: 'hello', ...(codecs.length ? { webCodecs: { version: 1, codecs } } : {}) };
    if (!this.send(message) && this.signalingLabel !== 'data' && this.dataOpen) {
      this.signalingLabel = 'data';
      this.send(message);
    }
  }

  /** A page-local port does not require Safari's missing OffscreenCanvas transfer API. */
  private hasWebCodecsSurface(): boolean {
    if (!this.target || !this.options || typeof VideoDecoder === 'undefined' ||
      typeof HTMLCanvasElement === 'undefined') return false;
    if (typeof HTMLCanvasElement.prototype.transferControlToOffscreen === 'function') return true;
    let port: WcWorkerPort | undefined;
    try {
      port = this.options.createWebCodecsWorker();
      return typeof port.startWithCanvas === 'function';
    } catch { return false; }
    finally { port?.terminate(); }
  }

  private async receive(message: NativeVideoMessage, raw: unknown): Promise<void> {
    if (message.type === 'fallback' || message.type === 'close') {
      const terminalFallback = webCodecsFallbackReason(message.reason);
      if (this.webCodecsSelected && !terminalFallback) {
        // A PlayJector restart retires this decoder generation, but it must not
        // hand presentation back to the primary Electron video sender. Hold the
        // last native canvas frame until the host configures the replacement.
        this.leaveNativePath(message.reason);
        this.publish();
        return;
      }
      this.webCodecsSelected = false;
      this.leaveNativePath(message.reason);
      this.publish();
      return;
    }
    if (message.type === 'configure' && message.codec) {
      this.webCodecsSelected = message.playback === 'webcodecs';
      this.markNativePending(message.codec, message.playback ?? 'rtp');
    }
    if (message.type === 'configure' && message.playback === 'webcodecs' && message.session) {
      this.client.close();
      this.session = message.session;
      if (!this.target || !this.options) { this.fail('WebCodecs surface or worker unavailable'); return; }
      await this.startWebCodecs();
    } else if (this.webCodecs && message.session === this.session) {
      if (message.type === 'answer' && message.sdp) await this.webCodecs.applyAnswer(message.sdp);
      else if (message.type === 'ice' && message.candidate) await this.webCodecs.addIceCandidate(message.candidate);
    } else this.client.receive(raw);
  }

  private async startWebCodecs(): Promise<void> {
    const target = this.target, options = this.options;
    if (!target || !options) throw new Error('WebCodecs presentation unavailable');
    const generation = ++this.generation, session = this.session;
    this.webCodecs?.close();
    this.webCodecs = null;
    let offered = false;
    const candidates: RTCIceCandidateInit[] = [];
    const client = new WebCodecsClient({
      createPeer: () => new RTCPeerConnection(this.peer.get().getConfiguration()),
      createWorker: () => options.createWebCodecsWorker(),
      nowUs: () => Math.round(performance.now() * 1000),
      onCandidate: candidate => {
        if (generation !== this.generation) return;
        if (offered) this.send({ nativeVideo: 1, type: 'ice', session, candidate });
        else if (candidates.length < 256) candidates.push(candidate);
        else this.fail('Native ICE queue exceeded');
      },
      onEvent: event => { if (generation === this.generation && !this.closed) this.event(event); },
    });
    this.webCodecs = client;
    client.attachCanvas(target.acquireCanvas());
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.visibility);
    this.visibility();
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.visibility);
    const offer = await client.start(session);
    if (generation !== this.generation || this.closed) return;
    if (!this.send({ nativeVideo: 1, type: 'offer', session, sdp: offer.sdp, playback: 'webcodecs' })) {
      this.fail('Native signaling unavailable'); return;
    }
    offered = true;
    for (const candidate of candidates) this.send({ nativeVideo: 1, type: 'ice', session, candidate });
  }

  private event(event: WcClientEvent): void {
    if (event.type === 'failed') { this.fail(event.reason); return; }
    if (event.type === 'unsupported') { this.fail('codec-unsupported'); return; }
    if (event.type === 'stalled') {
      if (this.selectedCanvas) this.patchPath({ recoveryState: 'keyframe' });
      return;
    }
    if (event.type === 'needsKeyframe') {
      if (this.selectedCanvas) this.patchPath({ recoveryState: 'keyframe' });
      return;
    }
    if (event.type === 'configured') { this.config = event.config; return; }
    const config = this.config;
    if (config && event.epoch === config.epoch && this.options?.onPresented) {
      const sample = paintSampleFrom(event, Math.round(performance.now() * 1000), Date.now());
      if (sample) this.options.onPresented(sample);
    }
    if (!event.first || !config || event.epoch !== config.epoch || !this.target) return;
    this.target.present({ kind: 'webcodecs', session: this.session, epoch: config.epoch,
      width: config.width, height: config.height, codec: config.codec });
    this.selectedCanvas = true;
    this.patchPath({
      active: 'native',
      pending: false,
      playback: 'webcodecs',
      codec: config.codec,
      recoveryState: 'idle',
    });
    this.send({ nativeVideo: 1, type: 'ready', session: this.session, playback: 'webcodecs',
      codec: config.codec, configEpoch: config.epoch, firstFrameId: event.frameId });
  }

  private send(message: NativeVideoMessage): boolean {
    return !this.closed && this.peer.send(JSON.stringify(message), this.signalingLabel ?? NATIVE_VIDEO_CHANNEL);
  }
  private publish(): void {
    if (this.closed) return;
    const stream = this.nativeTrack ? new MediaStream([...(this.primary?.getAudioTracks() ?? []), this.nativeTrack]) : this.primary;
    if (!stream) return;
    this.onStream(stream);
    if (!this.selectedCanvas && !this.webCodecsSelected) this.target?.present({ kind: 'stream', stream });
  }
  private fail(reason: string): void {
    const session = this.session;
    const normalized = reason === 'WebCodecs surface or worker unavailable' ? 'guest-unsupported' : reason;
    if (session) this.send({ nativeVideo: 1, type: 'fallback', session, reason: normalized.slice(0, 512) });
    this.leaveNativePath(normalized);
    this.retire();
  }
  private retire(): void {
    if (this.selectedCanvas) {
      this.pathStatus = {
        ...this.pathStatus,
        sessionRestarts: this.pathStatus.sessionRestarts + 1,
        recoveryState: 'restart',
      };
    }
    ++this.generation;
    this.webCodecs?.close(); this.webCodecs = null; this.config = null; this.session = '';
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.visibility);
    this.selectedCanvas = false;
    // The target delays removal of the last drawn image until a conventional frame is available.
    this.publish();
  }
  close(): void {
    if (this.closed) return;
    this.closed = true; ++this.helloGeneration;
    this.clearDataHello();
    if (this.helloDelay !== undefined) clearTimeout(this.helloDelay);
    this.retire(); this.client.destroy(); this.target?.releaseCanvas(); this.target = null;
  }
  getWebCodecsStats(): WcClientStats | null { return this.webCodecs?.getStats() ?? null; }
  getVideoPeerConnection(): RTCPeerConnection | null { return this.nativeTrack ? this.client.getPeerConnection() : null; }

  /** Snapshot for guest debug overlay (`formatVideoPathDebugLine`). */
  getVideoPathStatus(): VideoStreamingStatus {
    return { ...this.pathStatus };
  }

  private patchPath(extra: Partial<VideoStreamingStatus>): void {
    this.pathStatus = { ...this.pathStatus, ...extra };
  }

  private markNativePending(requestedCodec: NativeVideoCodec, playback: 'rtp' | 'webcodecs'): void {
    const webCodecs = playback === 'webcodecs';
    this.patchPath({
      active: webCodecs ? 'native' : 'electron',
      pending: true,
      requestedCodec,
      playback,
      codec: undefined,
      fallbackReason: undefined,
      reason: webCodecs ? this.pathStatus.reason : undefined,
      recoveryState: this.pathStatus.sessionRestarts > 0 ? 'restart' : 'idle',
    });
  }

  private leaveNativePath(reason?: string): void {
    if (this.webCodecsSelected) {
      // The host restarts WebCodecs rather than reverting; keep the failure visible until it does.
      this.patchPath({ active: 'native', pending: true, playback: 'webcodecs', codec: this.pathStatus.requestedCodec,
        recoveryState: 'idle', fallbackReason: undefined, reason: reason?.slice(0, 512) });
      return;
    }
    const fallbackReason = webCodecsFallbackReason(reason);
    this.patchPath({
      active: 'electron',
      pending: false,
      playback: undefined,
      codec: undefined,
      recoveryState: 'idle',
      ...(fallbackReason
        ? { fallbackReason, reason: undefined }
        : { fallbackReason: undefined, reason: reason?.slice(0, 512) }),
    });
  }
}
